import { auth, type AuthUser } from '$lib/auth.svelte';
import { request } from './client';

type LoginResponse = {
  token: string;
  user: AuthUser;
  sessionId: string;
};

/** POST /auth/login. On success, populates the auth store from the response so
 * subsequent requests carry the bearer token. */
export async function login(email: string, password: string): Promise<LoginResponse> {
  const result = await request<LoginResponse>('/auth/login', {
    method: 'POST',
    body: { email, password }
  });
  auth.setSession(result.token, result.user, result.sessionId);
  return result;
}

/** POST /auth/change-password — authenticated password change. Verifies the
 * current password, rotates the hash, and revokes every other session
 * belonging to the user (the calling browser stays logged in). */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request<void>('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword }
  });
}

/** POST /auth/forgot-password — starts the reset flow. The API always returns
 * 200 (we don't leak whether the email exists), so this returns void: callers
 * should display the same "check your email" message regardless. */
export async function forgotPassword(email: string): Promise<void> {
  await request<void>('/auth/forgot-password', {
    method: 'POST',
    body: { email }
  });
}

/** POST /auth/reset-password — complete the reset using the token from the
 * email. On success the server revokes every existing session for the user;
 * the local auth store is already empty on this page. */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await request<void>('/auth/reset-password', {
    method: 'POST',
    body: { token, newPassword }
  });
}

/** DELETE /sessions/current — revoke the calling token server-side and clear
 * the local store. We swallow request errors: if the server's already torn
 * down the session (or is unreachable), we still want to log the user out
 * locally. */
export async function logout(): Promise<void> {
  try {
    await request<void>('/sessions/current', { method: 'DELETE' });
  } catch {
    // Best effort — clear locally regardless.
  }
  auth.clear();
}
